import {
  DocumentPdfPane,
  type DocumentPaneRenderProps,
} from '@aecom-ai-engineering/react-components';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { type FC } from 'react';
import '@cyntler/react-doc-viewer/dist/index.css';

interface OfficeRendererPaneProps {
  active: boolean;
  src: DocumentPaneRenderProps['src'];
  caption?: string;
  fileName?: string;
  onSidebarContextChange?: DocumentPaneRenderProps['onSidebarContextChange'];
  convertedPdfUrl?: string;
}

/**
 * When the backend has already converted an Office document to PDF
 * (`convertedPdfFileId`), render via DocumentPdfPane. Otherwise fall back to
 * `@cyntler/react-doc-viewer` for client-side rendering.
 */
const OfficeRendererPane: FC<OfficeRendererPaneProps> = ({
  active,
  src,
  caption,
  fileName,
  onSidebarContextChange,
  convertedPdfUrl,
}) => {
  if (!active) return null;

  if (convertedPdfUrl) {
    return (
      <DocumentPdfPane
        active={active}
        caption={caption}
        fileName={fileName}
        onSidebarContextChange={onSidebarContextChange}
        src={convertedPdfUrl}
        searchable
        continuousScroll
      />
    );
  }

  const documentUri = typeof src === 'string' ? src : src instanceof URL ? src.href : undefined;
  if (!documentUri) return null;

  return (
    <div className="h-full w-full bg-white overflow-auto">
      <DocViewer
        documents={[{ uri: documentUri }]}
        pluginRenderers={DocViewerRenderers}
        className="h-full"
        config={{ header: { disableFileName: true } }}
      />
    </div>
  );
};

export interface OfficeRendererOptions {
  convertedPdfUrl?: string;
}

export function createOfficeRenderer(options?: OfficeRendererOptions) {
  return function OfficeRenderer(props: DocumentPaneRenderProps) {
    return (
      <OfficeRendererPane
        active={props.active}
        src={props.src}
        caption={props.caption}
        fileName={props.fileName}
        onSidebarContextChange={props.onSidebarContextChange}
        convertedPdfUrl={options?.convertedPdfUrl}
      />
    );
  };
}
